import { Link } from 'react-router-dom'
import CtaBand from '../components/CtaBand'
import Icon from '../components/Icon'
import PageHero from '../components/PageHero'
import Reveal from '../components/Reveal'
import Testimonials from '../components/sections/Testimonials'
import { Section, SectionHead } from '../components/Section'
import { ServiceArt } from '../components/art/LineArt'
import { company } from '../data/site'
import { useSeo } from '../lib/seo'

/** Who we are. All three paragraphs are verbatim from the old site. */
const ABOUT = [
  'Casa Hausverwaltung ist ein Unternehmen der B+K Immo Invest GmbH. Wir sind stolz darauf, zahlreiche eigene Objekte in der Region zu betreuen und unsere Erfahrung direkt aus der Praxis zu schöpfen.',
  'Als junges, vierköpfiges Team fassen wir die Anforderungen der Kunden als unsere auf, denn das Know-how stammt aus der Vermietung und Verwaltung eigener Immobilien. Durch digitale Prozesse und moderne Technologien werden wir unseren eigenen Ansprüchen gerecht.',
  'Der Kern unserer Unternehmenskultur ist die Messbarkeit von Qualitätsindikatoren, wobei wir stets die persönliche Ebene im Blick behalten. Casa - Hausverwaltung besser gemacht.',
]

/** How we work. */
const VALUES = [
  {
    icon: 'home',
    title: 'Erfahrung aus eigenem Bestand',
    text: 'Wir verwalten zahlreiche eigene Objekte in der Region. Das Know-how stammt aus der Praxis, nicht aus dem Lehrbuch.',
  },
  {
    icon: 'bolt',
    title: 'Digitale Prozesse',
    text: 'Moderne Werkzeuge und eine vernetzte Arbeitsweise halten Abläufe schnell und nachvollziehbar - für Sie und für Ihre Mieter.',
  },
  {
    icon: 'chart',
    title: 'Messbare Qualität',
    text: 'Der Kern unserer Unternehmenskultur ist die Messbarkeit von Qualitätsindikatoren. Was wir versprechen, prüfen wir nach.',
  },
  {
    icon: 'users',
    title: 'Persönliche Ebene',
    text: 'Bei allen Kennzahlen behalten wir den Menschen im Blick. Sie sprechen mit einem Team, das Ihr Objekt kennt.',
  },
]

export default function UeberUns() {
  useSeo({
    title: 'Über uns',
    description:
      'Casa Hausverwaltung ist ein Unternehmen der B+K Immo Invest GmbH: ein vierköpfiges Team, das seine Erfahrung aus der Verwaltung eigener Objekte in der Region schöpft.',
    path: '/ueber-uns',
  })

  /* Values read from data/site.js so the table cannot drift from the Impressum;
     only the labels are written here. */
  const rows = [
    ['Unternehmen', company.legalName],
    ['Geschäftsführung', company.managingDirectors.join(', ')],
    ['Zulassung', company.licence],
    ['Sitz', `${company.street}, ${company.postalCode} ${company.city}`],
    ['Handelsregister', `${company.registration}, ${company.registerCourt}`],
    ['USt-IdNr.', company.vatId],
  ]

  return (
    <>
      <PageHero
        eyebrow="Über uns"
        title="Lernen Sie uns kennen"
        lead="Ein junges Team, das die Anforderungen seiner Kunden kennt - weil es dieselben Aufgaben für eigene Objekte übernimmt."
        breadcrumb={[{ label: 'Über uns' }]}
      />

      <Section>
        <div className="split">
          <Reveal>
            <p className="eyebrow">Wer wir sind</p>
            <h2 style={{ marginTop: 'var(--s-4)' }}>
              Erfahrung aus der Praxis, nicht aus dem Lehrbuch
            </h2>
            {ABOUT.map((paragraph, index) => (
              <p
                key={paragraph.slice(0, 32)}
                style={{
                  marginTop: 'var(--s-5)',
                  ...(index === 0
                    ? {
                        fontSize: 'var(--step-1)',
                        lineHeight: 1.6,
                        color: 'var(--text)',
                      }
                    : null),
                }}
              >
                {paragraph}
              </p>
            ))}
          </Reveal>

          <Reveal
            className="service-row__visual"
            style={{ alignSelf: 'stretch', display: 'grid' }}
          >
            <ServiceArt variant="building" />
          </Reveal>
        </div>
      </Section>

      <Section tone="paper">
        <SectionHead
          eyebrow="Unsere Arbeitsweise"
          title="Vier Grundsätze, an denen wir uns messen lassen"
        />
        <Reveal group className="grid grid--2">
          {VALUES.map((value) => (
            <article className="card" key={value.title}>
              <div className="value">
                <span className="value__icon">
                  <Icon name={value.icon} />
                </span>
                <div>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </div>
              </div>
            </article>
          ))}
        </Reveal>
      </Section>

      <Section>
        <div className="split split--copy-narrow" style={{ alignItems: 'start' }}>
          <Reveal>
            <p className="eyebrow">Das Unternehmen</p>
            <h2 style={{ marginTop: 'var(--s-4)' }}>Zahlen und Fakten</h2>
            <p style={{ marginTop: 'var(--s-5)' }}>
              CASA Hausverwaltung ist die Verwaltungsmarke der{' '}
              {company.legalName}. Alle Angaben finden Sie vollständig im{' '}
              <Link to="/impressum">Impressum</Link>.
            </p>
          </Reveal>

          <Reveal>
            <div className="facts">
              {rows.map(([label, value]) => (
                <div className="facts__row" key={label}>
                  <span className="facts__key">{label}</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      <Testimonials />
      <CtaBand />
    </>
  )
}
